import { module } from 'modujs';
import { CAMERA_MODE } from '../utils/mixins/CamerasHandler';
import { $html } from '../utils/dom';
import { CUSTOM_EVENT, ENV } from '../config';
import { $screen } from '../stores/screen';

export default class extends module {
    constructor(m) {
        super(m);

        this.events = {
            click: {
                'start': 'start',
                'modeToggler': 'toggleMobileMode'
            },
            mouseleave: {
                'card': 'onCardLeave'
            },
            mouseenter: {
                'card': 'onCardFocus'
            },
            focusout: {
                'card': 'onCardLeave'
            },
            focusin: {
                'card': 'onCardFocus'
            }            
        }

        this.$cards = this.$('card');
        this.$list = this.$('list')[0]
        this.$modeToggler = this.$('modeToggler')[0]
        this.mobileModeLabels = this.getData('mobile-mode-labels').split(',');
    }

    init() {
        $html.classList.add('has-media-hero-inview')

        // Prevent "start" while ExploreScene is not initialized
        setTimeout(() => {
            this.readyToPassSplash = true
            this.$('start')[0]?.classList.remove('-loading')
        }, 1250)
    }    

    // Allows to get out of splash
    start(instant = false) {
        if(!this.readyToPassSplash) return

        // Prevent more than one call
        if(this.started) return;
        this.started = true

        this.el.classList.remove('-has-splash') // Disable splash
        this.call(instant === true ? 'setCameraModeInstant' : 'setCameraMode', CAMERA_MODE.PANEL, 'ExploreScene'); // Camera to PANEL
        this.call('start', null, 'ExploreUi')

        this.refreshMobileMode();        

        this.bindEvents();
    }

    refreshMobileMode() {
        if(window.innerWidth < 700) {
            const expanded = this.el.classList.contains('show-mobile-list')
            this.$list.setAttribute('aria-expanded', expanded);
            this.$modeToggler.setAttribute('aria-label', this.mobileModeLabels[expanded ? 1 : 0]);
        } else {
            this.el.classList.remove('show-mobile-list');
            this.$list.removeAttribute('aria-expanded')
            this.$modeToggler.removeAttribute('aria-label')
        }
    }

    toggleMobileMode() {
        this.$list.scrollTop = 0
        this.el.classList.toggle('show-mobile-list');
        this.refreshMobileMode();
    }

    bindEvents() {
        this.onPinFocus = this.onPinFocus.bind(this)
        this.onPinLeave = this.onPinLeave.bind(this)
        window.addEventListener(CUSTOM_EVENT.EXPLORE_PIN_ENTER, this.onPinFocus)
        window.addEventListener(CUSTOM_EVENT.EXPLORE_PIN_LEAVE, this.onPinLeave)
        
        this.unbindScreenListener = $screen.subscribe(this.onResize.bind(this))
    }

    unbindEvents() {
        window.removeEventListener(CUSTOM_EVENT.EXPLORE_PIN_ENTER, this.onPinFocus)
        window.removeEventListener(CUSTOM_EVENT.EXPLORE_PIN_LEAVE, this.onPinLeave)
    }

    onCardLeave(e) {        
        if(this.interactionsPrevented) return;

        this.blurLocation();
    }

    onCardFocus(e) {
        if(this.interactionsPrevented) return;

        const target = e.curTarget;
        const id = this.getData('id', target);

        this.focusLocation({id});
    }

    onPinFocus(e) {
        if(this.interactionsPrevented) return;

        const {id} = e.detail
        this.focusLocation({id, goToCoordinates: false, autoScroll: true});
    }

    onPinLeave() {
        if(this.interactionsPrevented) return;

        this.blurLocation()
    }    

    onResize() {
        this.refreshMobileMode();
    }

    setTabByObjectId(id) {
        // Query the card matching the ID
        const card = Array.from(this.$cards).find(item => {
            const itemId = this.getData('id', item)
            return id == itemId; 
        })

        if(card) {
            const type = this.getData('type', card);
            this.call('setSelectedTabBySlug', type, 'ExploreUi')
        } else {
            throw 'Card not found'
        }
    }

    focusLocation({id, goToCoordinates = true, autoScroll = false, exclusive = false, duration = ENV.PREFERS_REDUCED_MOTION ? 0 : null }) {
        // Fade other pins
        this.el.classList.add('has-focused-location')
        if(exclusive) // If exclusive, completely hide other pins
            this.el.classList.add('has-exclusive-location')

        // Pause auto rotation otherwise the user would need to keep following the pin and it could cause glitches
        this.call('pauseAutoRotate', null, 'ExploreScene')

        // Loop over the cards to try and find the one matching `id`
        for(let $card of this.$cards) {            
            const cardId = this.getData('id', $card) // Get the id
            const isTarget = id == cardId // Store if it's the target
            $card.classList.toggle('is-active', isTarget); // Enable/disable
                        
            if(isTarget) {                             
                if(autoScroll) {
                    $card.scrollIntoView({
                        behavior: duration == 0 ? 'instant' : 'smooth',
                        block: 'center'
                    });
                }
            }
        }

        // Forward the call to `ExploreScene` to focus the pin and go to its coordinates
        this.call('focusLocation', { id, goToCoordinates, duration }, 'ExploreScene')
    }

    blurLocation() {
        // Stop focusing on a single pin
        this.el.classList.remove('has-focused-location')
        this.el.classList.remove('has-exclusive-location')

        // Resume autoRotate
        this.call('resumeAutoRotate', null, 'ExploreScene')

        // Make all cards inactive
        for(let $card of this.$cards) {
            $card.classList.toggle('is-active', false);
        }

        // Inform explore scene to make all pins inactive
        this.call('blurLocation', null, 'ExploreScene')
    }

    preventInteractions(value) {
        this.interactionsPrevented = value;
        this.el.classList.toggle('is-not-interactive', this.interactionsPrevented)
    }

    destroy() {
        super.destroy()

        this.unbindEvents();
    }
}
